import { type MouseEvent, type PointerEvent, useRef, useCallback } from 'react';

/**
 * Hook that provides fast-tap activation for mobile WebView/iframe environments.
 *
 * Returns `onPointerDown` and `onClick` handlers that ensure immediate response
 * to touch/pointer interactions while preserving keyboard accessibility.
 *
 * - `onPointerDown` fires immediately on touch contact (before the delayed `click` event)
 * - `onClick` fires as fallback for keyboard (Enter/Space) and non-pointer interactions
 * - A ref prevents double-execution when both events fire for the same interaction
 */
export function usePointerActivation(action: () => void) {
	const pointerActivatedRef = useRef(false);

	const onPointerDown = useCallback(
		(e: PointerEvent) => {
			if (!e.isPrimary || e.pointerType === 'mouse') return;
			pointerActivatedRef.current = true;
			action();
		},
		[action]
	);

	const onClick = useCallback(
		(e: MouseEvent) => {
			if (pointerActivatedRef.current) {
				pointerActivatedRef.current = false;
				e.preventDefault();
				return;
			}
			action();
		},
		[action]
	);

	return { onPointerDown, onClick };
}
